<template>
  <Teleport to="body">
    <v-dialog
      :model-value="modelValue"
      max-width="440"
      persistent
      class="caller-dialog-root"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <SnackbarComp :SnackbarComponent="snackbar" />

      <div
        class="caller-dialog"
        :class="{ 'caller-dialog--danger': action === 'delete' }"
        role="dialog"
        aria-modal="true"
      >
        <header class="caller-dialog__header">
          <div class="caller-dialog__brand">
            <div
              class="caller-dialog__icon"
              :class="{ 'caller-dialog__icon--danger': action === 'delete' }"
              aria-hidden="true"
            >
              <v-icon :icon="icon" size="22" color="white" />
            </div>
            <div>
              <h2 class="caller-dialog__title">{{ title }}</h2>
              <p class="caller-dialog__subtitle">{{ subtitle }}</p>
            </div>
          </div>
          <button
            type="button"
            class="caller-dialog__close"
            aria-label="Close"
            :disabled="saving"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="caller-dialog__body caller-dialog__body--center">
          <p class="caller-dialog__confirm">
            {{ confirmLead }}
            <strong>{{ contextName }}</strong>?
          </p>
        </div>

        <footer class="caller-dialog__footer">
          <v-btn
            variant="text"
            class="caller-dialog__cancel"
            :disabled="saving"
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            class="caller-dialog__submit"
            :class="{ 'caller-dialog__submit--danger': action === 'delete' }"
            :loading="saving"
            @click="submit"
          >
            {{ confirmLabel }}
          </v-btn>
        </footer>
      </div>
    </v-dialog>
  </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'ContextConfirmDialog' })

const ACTION_COPY = {
  enable: {
    icon: 'mdi-play-circle-outline',
    title: 'Enable product',
    subtitle: 'This product will become available for use again.',
    confirmLead: 'Are you sure you want to enable',
    confirmLabel: 'Enable product',
    errorFallback: 'Failed to enable product',
    successText: 'Product enabled successfully.',
  },
  disable: {
    icon: 'mdi-pause-circle-outline',
    title: 'Disable product',
    subtitle: 'This product will no longer be available for use.',
    confirmLead: 'Are you sure you want to disable',
    confirmLabel: 'Disable product',
    errorFallback: 'Failed to disable product',
    successText: 'Product disabled successfully.',
  },
  delete: {
    icon: 'mdi-trash-can-outline',
    title: 'Delete product',
    subtitle: 'This action cannot be undone.',
    confirmLead: 'Are you sure you want to delete',
    confirmLabel: 'Delete product',
    errorFallback: 'Failed to delete product',
    successText: 'Product deleted successfully.',
  },
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  action: {
    type: String,
    default: 'delete',
    validator: (value) => ['enable', 'disable', 'delete'].includes(value),
  },
  context: { type: Object, default: null },
  confirmFn: { type: Function, required: true },
})

const emit = defineEmits(['update:modelValue', 'confirmed'])

const saving = ref(false)
const snackbar = ref({})

const contextName = computed(
  () => props.context?.context_name || 'this product',
)

const copy = computed(
  () => ACTION_COPY[props.action] || ACTION_COPY.delete,
)

const icon = computed(() => copy.value.icon)
const title = computed(() => copy.value.title)
const subtitle = computed(() => copy.value.subtitle)
const confirmLead = computed(() => copy.value.confirmLead)
const confirmLabel = computed(() => copy.value.confirmLabel)

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  saving.value = true
  try {
    const response = await props.confirmFn(props.context)
    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response, copy.value.errorFallback),
      }
      return
    }

    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: copy.value.successText,
    }
    emit('confirmed')
    close()
  } catch (error) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: getApiMessage(error, 'Request failed'),
    }
  } finally {
    saving.value = false
  }
}
</script>
